import React, { useContext, useState } from 'react';
import { get } from 'lodash';

import Button from '@react/react-spectrum/Button';
import Checkbox from '@react/react-spectrum/Checkbox';
import Heading from '@react/react-spectrum/Heading';
import Link from '@react/react-spectrum/Link';
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger';
import Popover from '@react/react-spectrum/Popover';

import InfoOutline from '@react/react-spectrum/Icon/InfoOutline';

import { QuickSetupContext } from './Context';
import { FeaturesContext } from '../../features/FeaturesContext';
import { useAepSandboxSettings } from 'root/hooks';

export default function Advanced() {
  const { context } = useContext(QuickSetupContext);
  const settings = useAepSandboxSettings(context?.environment?.id);
  const [show, setShow] = useState(false);

  if (settings?.quickSetup?.disableAdvanced) {
    return (
      <div>
        Advanced configuration options are not available for selected
        environment
      </div>
    );
  }

  return (
    <div className="details">
      <div className="heading">
        <Heading size={3}>Advanced</Heading>
        <Link onClick={() => setShow(!show)}>{show ? 'Hide' : 'Show'}</Link>
      </div>

      {show && (
        <div className="fields">
          {!context.configuration && <em>Please select configuration</em>}
          {context.configuration && (
            <>
              <DetailsList />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function DetailsList() {
  const { context } = useContext(QuickSetupContext);
  const { isFeatureEnabled, SOURCES_CONFIGURATION, QS_DATA_IMPORT } =
    useContext(FeaturesContext);
  const {
    aep,
    dataImport,
    sources,
    ajo,
    offers,
    exd,
    launch,
    projects,
    loyalty
  } = context.configuration?.content;

  return (
    <div className="details-list">
      {aep && <DetailsSection title="Core AEP" options={aep} path="aep" />}
      {isFeatureEnabled(QS_DATA_IMPORT) && dataImport && (
        <DetailsSection
          title="Data Import"
          options={dataImport}
          path="dataImport"
        />
      )}
      {isFeatureEnabled(SOURCES_CONFIGURATION) && sources && (
        <DetailsSection
          title="Sources & Destinations"
          options={sources}
          path="sources"
        />
      )}
      {offers && (
        <DetailsSection
          title="Offer Decisioning"
          options={offers}
          path="offers"
        />
      )}
      {exd && (
        <DetailsSection
          title="Experience Decisioning"
          options={exd}
          path="exd"
        />
      )}
      {ajo && (
        <DetailsSection title="Journey Optimizer" options={ajo} path="ajo" />
      )}
      {loyalty && (
        <DetailsSection title="AJO Loyalty" options={loyalty} path="loyalty" />
      )}
      {projects && (
        <DetailsSection title="Projects" options={projects} path="projects" />
      )}
      {launch && (
        <DetailsSection
          title="Data Collection (Launch)"
          options={launch}
          path="launch"
        />
      )}
    </div>
  );
}

function DetailsSection({ title, options, path }) {
  const { variants, channels } = options;

  return (
    <div className="details-section">
      <Heading size={4}>{title}</Heading>

      <VariantList data={variants} path={path} />
      <ChannelList data={channels} path={path} />
    </div>
  );
}

function VariantList({ data, path }) {
  const { context, setContextValue } = useContext(QuickSetupContext);

  const variantSelected = (name) => {
    const variants = get(context, `selectedValues.${path}.variants`);
    return Array.isArray(variants) ? variants.includes(name) : false;
  };

  const toggleVariant = (name) => {
    const variants = get(context, `selectedValues.${path}.variants`);

    if (Array.isArray(variants)) {
      const value = variants.includes(name)
        ? variants.filter((v) => v !== name)
        : [...variants, name];

      setContextValue({
        path: `selectedValues.${path}.variants`,
        value
      });
    }
  };

  return (
    Array.isArray(data?.values) && (
      <div>
        <Heading size={5}>Variants</Heading>
        <div className="options">
          {data.values?.map((variant) => (
            <div className="option" key={`variant-${variant.name}`}>
              <Checkbox
                value={variant.name}
                checked={variantSelected(variant.name)}
                onChange={() => toggleVariant(variant.name)}>
                {variant.title}
              </Checkbox>
              {variant.description && (
                <DescriptionOverlay content={variant.description} />
              )}
            </div>
          ))}
        </div>
      </div>
    )
  );
}

function ChannelList({ data, path }) {
  const { context, setContextValue } = useContext(QuickSetupContext);

  const channelSelected = (name) => {
    const channels = get(context, `selectedValues.${path}.channels`);
    return Array.isArray(channels) ? channels.includes(name) : false;
  };

  const toggleChannel = (name) => {
    const channels = get(context, `selectedValues.${path}.channels`);

    if (Array.isArray(channels)) {
      const value = channels.includes(name)
        ? channels.filter((v) => v !== name)
        : [...channels, name];

      setContextValue({
        path: `selectedValues.${path}.channels`,
        value
      });
    }
  };

  return (
    Array.isArray(data?.values) && (
      <div>
        <Heading size={5}>Channels</Heading>
        <div className="options">
          {data.values?.map((channel) => (
            <div className="option" key={`channel-${channel.name}`}>
              <Checkbox
                value={channel.name}
                checked={channelSelected(channel.name)}
                onChange={() => toggleChannel(channel.name)}>
                {channel.title}
              </Checkbox>
              {channel.description && (
                <DescriptionOverlay content={channel.description} />
              )}
            </div>
          ))}
        </div>
      </div>
    )
  );
}

function DescriptionOverlay({ content }) {
  return (
    <OverlayTrigger placement="right">
      <Button icon={<InfoOutline size="XS" />} variant="tool" />
      <Popover variant="default">{content}</Popover>
    </OverlayTrigger>
  );
}
